import type {
    SiteBuilderBlock,
    SiteBuilderContentJson,
} from "./siteBuilderTypes";

function textValue(value: unknown): string | null {
    if (typeof value !== "string") {
        return null;
    }

    const trimmed = value.trim();

    return trimmed === "" ? null : trimmed;
}

/** Stable backend purpose used by forms created directly from the builder. */
export function siteBuilderGeneratedFormKey(blockUuid: string): string {
    const normalizedUuid = blockUuid
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9-]+/g, "-")
        .replace(/^-+|-+$/g, "");

    return `builder-${normalizedUuid}`.slice(0, 64);
}

/**
 * A duplicated builder-owned form must receive its own backend identity.
 * Explicit admin and preset purposes are intentionally preserved.
 */
export function withRegeneratedSiteBuilderFormIdentity(
    block: SiteBuilderBlock,
    previousUuid: string,
    nextUuid: string,
): SiteBuilderBlock {
    if (block.type !== "form") {
        return block;
    }

    const previousKey = siteBuilderGeneratedFormKey(previousUuid);
    const formKey = textValue(block.content?.formKey);
    const purpose = textValue(block.content?.purpose);
    const generatedIdentity = (
        (formKey === previousKey && (purpose === null || purpose === previousKey))
        || (formKey === null && purpose === previousKey)
    );

    if (!generatedIdentity) {
        return block;
    }

    const nextKey = siteBuilderGeneratedFormKey(nextUuid);

    return {
        ...block,
        content: {
            ...(block.content ?? {}),
            formKey: nextKey,
            purpose: nextKey,
        },
    };
}

const DETERMINISTIC_UUID_HASH_SEEDS = [
    0x811c9dc5,
    0x9e3779b9,
    0x85ebca6b,
    0xc2b2ae35,
] as const;

function existingBlockUuid(block: SiteBuilderBlock): string | null {
    return typeof block.uuid === "string" && block.uuid.trim() !== ""
        ? block.uuid
        : null;
}

function blockStructuralPath(
    parentPath: string,
    collection: "children" | "slots",
    index: number,
    slot?: string,
): string {
    return collection === "children"
        ? `${parentPath}.children.${index}`
        : `${parentPath}.slots.${encodeStructuralPathSegment(slot ?? "")}.${index}`;
}

/**
 * PHP's rawurlencode() follows RFC 3986, while JavaScript's
 * encodeURIComponent() deliberately leaves !'()* unescaped. The backend
 * materializer uses rawurlencode(), so normalize those five characters here
 * as well to keep deterministic identities byte-for-byte portable.
 */
function encodeStructuralPathSegment(value: string): string {
    return encodeURIComponent(value).replace(
        /[!'()*]/gu,
        (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`,
    );
}

function visitSiteBuilderBlocks(
    blocks: readonly SiteBuilderBlock[],
    visit: (block: SiteBuilderBlock, path: string) => void,
    parentPath: string = "blocks",
): void {
    blocks.forEach((block, index) => {
        const path = `${parentPath}.${index}`;
        visitSiteBuilderBlock(block, path, visit);
    });
}

function visitSiteBuilderBlock(
    block: SiteBuilderBlock,
    path: string,
    visit: (block: SiteBuilderBlock, path: string) => void,
): void {
    visit(block, path);

    block.children?.forEach((child, index) => {
        visitSiteBuilderBlock(
            child,
            blockStructuralPath(path, "children", index),
            visit,
        );
    });

    Object.entries(block.slots ?? {}).forEach(([slot, children]) => {
        children.forEach((child, index) => {
            const childPath = blockStructuralPath(
                path,
                "slots",
                index,
                slot,
            );
            visitSiteBuilderBlock(child, childPath, visit);
        });
    });
}

function deterministicHashWord(value: string, seed: number): string {
    let hash = seed >>> 0;
    const bytes = new TextEncoder().encode(value);

    bytes.forEach((byte) => {
        hash ^= byte;
        hash = Math.imul(hash, 0x01000193) >>> 0;
    });

    return hash.toString(16).padStart(8, "0");
}

/**
 * Produces a stable UUID-shaped identity without relying on browser state.
 * The namespace must describe the owning aggregate and editable region; the
 * structural path deliberately excludes localized content and language code.
 */
export function createDeterministicSiteBuilderBlockUuid(
    namespace: string,
    structuralPath: string,
): string {
    const input = `${namespace}\u001f${structuralPath}`;
    const digest = DETERMINISTIC_UUID_HASH_SEEDS
        .map((seed, index) => deterministicHashWord(`${index}:${input}`, seed))
        .join("");
    const versioned = `${digest.slice(0, 12)}5${digest.slice(13)}`;
    const variant = (
        (Number.parseInt(versioned[16] ?? "0", 16) & 0x3) | 0x8
    ).toString(16);
    const normalized = `${versioned.slice(0, 16)}${variant}${versioned.slice(17)}`;

    return [
        normalized.slice(0, 8),
        normalized.slice(8, 12),
        normalized.slice(12, 16),
        normalized.slice(16, 20),
        normalized.slice(20, 32),
    ].join("-");
}

function documentIdentityMap(
    document: SiteBuilderContentJson | null | undefined,
): Map<string, string> {
    const identities = new Map<string, string>();

    visitSiteBuilderBlocks(document?.blocks ?? [], (block, path) => {
        const uuid = existingBlockUuid(block);

        if (uuid) {
            identities.set(path, uuid);
        }
    });

    return identities;
}

function materializedBlock(
    block: SiteBuilderBlock,
    path: string,
    identities: ReadonlyMap<string, string>,
): SiteBuilderBlock {
    const uuid = existingBlockUuid(block) ?? identities.get(path);
    let children = block.children;
    let slots = block.slots;

    if (block.children) {
        const nextChildren = block.children.map((child, index) => (
            materializedBlock(
                child,
                blockStructuralPath(path, "children", index),
                identities,
            )
        ));

        if (nextChildren.some((child, index) => child !== block.children?.[index])) {
            children = nextChildren;
        }
    }

    if (block.slots) {
        let slotsChanged = false;
        const nextSlots = Object.fromEntries(
            Object.entries(block.slots).map(([slot, slotBlocks]) => {
                const nextBlocks = slotBlocks.map((child, index) => (
                    materializedBlock(
                        child,
                        blockStructuralPath(path, "slots", index, slot),
                        identities,
                    )
                ));
                const slotChanged = nextBlocks.some(
                    (child, index) => child !== slotBlocks[index],
                );

                slotsChanged = slotsChanged || slotChanged;

                return [slot, slotChanged ? nextBlocks : slotBlocks];
            }),
        );

        if (slotsChanged) {
            slots = nextSlots;
        }
    }

    if (
        uuid === block.uuid
        && children === block.children
        && slots === block.slots
    ) {
        return block;
    }

    return {
        ...block,
        uuid,
        ...(children ? { children } : {}),
        ...(slots ? { slots } : {}),
    };
}

/**
 * Materializes missing identities with structural sharing:
 *
 * - an existing non-empty UUID is preserved byte-for-byte;
 * - a reference document may donate an identity at the same structural path;
 * - all remaining identities are deterministic for namespace + path;
 * - a document whose complete subtree already has identities is returned
 *   unchanged by reference.
 */
export function materializeSiteBuilderDocumentBlockUuids(
    document: SiteBuilderContentJson,
    namespace: string,
    referenceDocument?: SiteBuilderContentJson | null,
): SiteBuilderContentJson {
    const blocks = document.blocks ?? [];
    const existingIdentities = documentIdentityMap(document);
    const referenceIdentities = documentIdentityMap(referenceDocument);
    const usedUuids = new Set(existingIdentities.values());
    const identities = new Map(existingIdentities);
    const missingPaths: string[] = [];

    visitSiteBuilderBlocks(blocks, (block, path) => {
        if (!existingBlockUuid(block)) {
            missingPaths.push(path);
        }
    });

    missingPaths.sort().forEach((path) => {
        const referencedUuid = referenceIdentities.get(path);

        if (referencedUuid && !usedUuids.has(referencedUuid)) {
            identities.set(path, referencedUuid);
            usedUuids.add(referencedUuid);
            return;
        }

        let attempt = 0;
        let uuid = createDeterministicSiteBuilderBlockUuid(namespace, path);

        while (usedUuids.has(uuid)) {
            attempt += 1;
            uuid = createDeterministicSiteBuilderBlockUuid(
                namespace,
                `${path}#${attempt}`,
            );
        }

        identities.set(path, uuid);
        usedUuids.add(uuid);
    });

    const nextBlocks = blocks.map((block, index) => (
        materializedBlock(block, `blocks.${index}`, identities)
    ));

    if (nextBlocks.every((block, index) => block === blocks[index])) {
        return document;
    }

    return {
        ...document,
        blocks: nextBlocks,
    };
}

export function createSiteBuilderBlockUuid(): string {
    if (typeof globalThis.crypto?.randomUUID === "function") {
        return globalThis.crypto.randomUUID();
    }

    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
        const random = Math.floor(Math.random() * 16);
        const value = character === "x" ? random : (random & 0x3) | 0x8;

        return value.toString(16);
    });
}

export function withSiteBuilderBlockUuids(block: SiteBuilderBlock): SiteBuilderBlock {
    const slots = block.slots
        ? Object.fromEntries(Object.entries(block.slots).map(([key, blocks]) => [
            key,
            blocks.map(withSiteBuilderBlockUuids),
        ]))
        : undefined;
    const children = block.children?.map(withSiteBuilderBlockUuids);

    return {
        ...block,
        uuid: typeof block.uuid === "string" && block.uuid !== "" ? block.uuid : createSiteBuilderBlockUuid(),
        ...(slots ? { slots } : {}),
        ...(children ? { children } : {}),
    };
}

export function regenerateSiteBuilderBlockUuids(block: SiteBuilderBlock): SiteBuilderBlock {
    const slots = block.slots
        ? Object.fromEntries(Object.entries(block.slots).map(([key, blocks]) => [
            key,
            blocks.map(regenerateSiteBuilderBlockUuids),
        ]))
        : undefined;
    const children = block.children?.map(regenerateSiteBuilderBlockUuids);

    const previousUuid = block.uuid ?? "";
    const nextUuid = createSiteBuilderBlockUuid();
    const regenerated = {
        ...block,
        uuid: nextUuid,
        ...(slots ? { slots } : {}),
        ...(children ? { children } : {}),
    };

    return withRegeneratedSiteBuilderFormIdentity(
        regenerated,
        previousUuid,
        nextUuid,
    );
}
